import { useEffect, useRef } from 'react';
import type { ProductPlan } from '@/features/products/types';
import { ANALYTICS_EVENTS, trackEvent } from '@/features/analytics';
import hero from '../assets/priority/hero.webp';
import adv1 from '../assets/priority/adv-1.svg';
import adv2 from '../assets/priority/adv-2.svg';
import adv3 from '../assets/priority/adv-3.svg';
import adv4 from '../assets/priority/adv-4.svg';
import reviewerMarcus from '../assets/reviews/marcus.webp';
import reviewerEthan from '../assets/reviews/ethan.webp';
import reviewerKarima from '../assets/reviews/karima.webp';
import { useStickyCta } from '../hooks/use-sticky-cta';
import { OfferStepperHeader } from './offer-stepper-header';
import { OfferAdvantages } from './offer-advantages';
import { OfferPayment } from './offer-payment';
import { OfferDisclaimer } from './offer-disclaimer';
import { OfferReviews } from './offer-reviews';
import { OfferSatisfaction } from './offer-satisfaction';
import { OfferStickyCta } from './offer-sticky-cta';
import { priorityOfferDisclaimer } from '../domain/disclaimer';

const ADVANTAGES = [
  { icon: adv1, before: 'Skip the wait list and get your claims ', lead: 'filed within 24 hours' },
  {
    icon: adv2,
    before: 'Earlier filing means a ',
    lead: 'better position',
    after: ' when settlement funds are paid out',
  },
  {
    icon: adv3,
    before: 'Get ',
    lead: 'priority deadline alerts',
    after: ' so you never miss an eligible claim',
  },
  {
    icon: adv4,
    lead: 'Maximize what you recover',
    after: ' from time-sensitive settlements before they close',
  },
];

const REVIEWS = [
  {
    avatar: reviewerMarcus,
    name: 'Marcus T.',
    text: "Honestly wasn't sure it'd make a difference but my claim got filed way faster than I expected. Worth it just for the peace of mind.",
  },
  {
    avatar: reviewerEthan,
    name: 'Ethan P.',
    text: 'Skipped the wait completely. I unlocked it and my claim was filed within the day, no back and forth needed.',
  },
  {
    avatar: reviewerKarima,
    name: 'Karima S.',
    text: "Wasn't expecting much honestly but it actually moved things along fast. Got priority alerts too so I never had to check manually.",
  },
];

interface PriorityOfferPageProps {
  offerLabels: string[];
  currentIndex: number;
  plan: ProductPlan | undefined;
  purchasing: boolean;
  error?: string | null;
  onUnlock: () => void;
  onSkip: () => void;
}

export function PriorityOfferPage({
  offerLabels,
  currentIndex,
  plan,
  purchasing,
  error,
  onUnlock,
  onSkip,
}: PriorityOfferPageProps) {
  const { rootRef, stickyHidden } = useStickyCta(currentIndex);
  const firedShown = useRef(false);

  useEffect(() => {
    if (firedShown.current) return;
    firedShown.current = true;
    trackEvent(ANALYTICS_EVENTS.SETTLEMENT_PRIORITY_SHOWN);
    if (!plan) return;
    trackEvent(ANALYTICS_EVENTS.PURCHASE_PAGE_VIEWED, {
      productId: plan.id,
      price: plan.price,
      currency: plan.currency,
      metadata: { kind: 'additional_offer' },
    });
  }, [plan]);

  const total = plan?.price ?? 0;
  const previous = plan?.previousPrice;
  const currency = plan?.currency ?? 'USD';
  const disclaimer = priorityOfferDisclaimer(plan);

  const payment = (
    <OfferPayment
      total={total}
      previousTotal={previous}
      currency={currency}
      discountPercent={plan?.discountPercent}
      ctaLabel="Get Priority Access"
      purchasing={purchasing}
      error={error}
      onCta={onUnlock}
    />
  );

  const intro = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 text-center">
        <h1 className="font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
          Get Filed First With <span className="text-mf-green">Priority Access</span>
        </h1>
        <p className="font-body text-base font-normal leading-5 text-mf-neutral-600">
          Skip the standard queue and get your eligible claims filed within 24 hours
        </p>
      </div>
      <img src={hero} alt="" className="h-auto w-full" />
      <OfferAdvantages items={ADVANTAGES} />
    </div>
  );

  return (
    <div className="min-h-[100dvh] w-full bg-mf-neutral-50 font-body text-mf-neutral-900">
      <div
        ref={rootRef}
        className="mx-auto flex min-h-[100dvh] max-w-app flex-col bg-mf-neutral-50"
      >
        <OfferStepperHeader
          offerLabels={offerLabels}
          currentIndex={currentIndex}
          unlockedLabel="Exclusive Priority Access unlocked"
          onSkip={onSkip}
        />

        <div className="flex flex-col gap-8 px-4 pb-10 pt-6">
          <div className="flex flex-col gap-4">
            {intro}
            {payment}
          </div>
          <OfferDisclaimer copy={disclaimer} />
          <OfferReviews
            title={
              <>
                What users say after
                <br />
                unlocking <span className="text-mf-green">Priority Access</span>
              </>
            }
            reviews={REVIEWS}
          />
          <div className="h-px w-full bg-mf-neutral-200" />
          <OfferSatisfaction />
          <div className="h-px w-full bg-mf-neutral-200" />
          {intro}
          {payment}
          <OfferDisclaimer copy={disclaimer} />
        </div>
      </div>

      <OfferStickyCta
        label="Get Priority Access"
        onClick={onUnlock}
        purchasing={purchasing}
        hidden={stickyHidden}
      />
    </div>
  );
}
